/**
 * My Yacht Page
 * View and edit yacht profile (one yacht per captain)
 */

import React, { useState, useEffect } from 'react';
import { yachtsAPI } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  Ship,
  Edit2,
  X,
  Check,
  Anchor,
} from 'lucide-react';

function MyYachts() {
  const { showToast } = useNotifications();
  const [yacht, setYacht] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    yacht_type: 'motor',
    length: '',
  });

  useEffect(() => {
    loadYacht();
  }, []);

  const loadYacht = async () => {
    try {
      const data = await yachtsAPI.getMyYachts();
      // Get the first (and only) yacht
      const userYacht = data.yachts?.[0] || data[0] || null;
      setYacht(userYacht);
      if (userYacht) {
        setFormData({
          name: userYacht.name || '',
          yacht_type: userYacht.yacht_type || 'motor',
          length: userYacht.length_m?.toString() || userYacht.length?.toString() || '',
        });
      }
    } catch (error) {
      console.error('Failed to load yacht:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const yachtData = {
        name: formData.name,
        yacht_type: formData.yacht_type,
        length_m: parseFloat(formData.length),
      };

      await yachtsAPI.updateYacht(yacht.id, yachtData);
      showToast('Yacht updated successfully', 'success');
      setShowForm(false);
      loadYacht();
    } catch (error) {
      showToast(error.message || 'Failed to save yacht', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    // Reset form to current yacht data
    if (yacht) {
      setFormData({
        name: yacht.name || '',
        yacht_type: yacht.yacht_type || 'motor',
        length: yacht.length_m?.toString() || yacht.length?.toString() || '',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!yacht) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card text-center py-12">
          <Ship className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-navy-900 mb-2">
            No yacht found
          </h3>
          <p className="text-navy-600">
            Your yacht information will appear here once set up.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-navy-900">
          My Yacht
        </h1>
        <p className="text-navy-600 mt-1">
          Your vessel details
        </p>
      </div>

      {/* Yacht Card */}
      <div className="card">
        <div className="flex items-start justify-between">
          <div className="flex items-start">
            <div className="h-16 w-16 bg-primary-100 rounded-xl flex items-center justify-center">
              <Ship className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-5">
              <h2 className="text-xl font-semibold text-navy-900">
                {yacht.name}
              </h2>
              <p className="text-navy-600 capitalize mt-1">
                {yacht.yacht_type} Yacht
              </p>
              <div className="mt-3 flex items-center text-navy-500">
                <Anchor className="h-4 w-4 mr-2" />
                <span>{yacht.length_m || yacht.length}m length</span>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowForm(true)}
            className="btn-secondary"
          >
            <Edit2 className="h-4 w-4 mr-2" />
            Edit
          </button>
        </div>
      </div>

      {/* Edit Yacht Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-display font-semibold text-navy-900">
                Edit Yacht
              </h2>
              <button
                onClick={closeForm}
                className="p-2 text-navy-400 hover:text-navy-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Yacht Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="e.g., Ocean Dream"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Yacht Type
                </label>
                <select
                  name="yacht_type"
                  value={formData.yacht_type}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="motor">Motor</option>
                  <option value="sailing">Sailing</option>
                  <option value="catamaran">Catamaran</option>
                  <option value="superyacht">Superyacht</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Length (meters)
                </label>
                <input
                  type="number"
                  name="length"
                  value={formData.length}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="12"
                  step="0.1"
                  min="1"
                  required
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeForm}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="btn-primary"
                >
                  {formLoading ? (
                    <span className="flex items-center">
                      <span className="spinner h-5 w-5 mr-2" />
                      Saving...
                    </span>
                  ) : (
                    <>
                      <Check className="h-5 w-5 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyYachts;
